import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useApi } from '../../context/ApiProvider';
import { Link, useSearchParams } from 'react-router-dom';
import SearchClients from '../SearchClients';
import FilterModal from '../Helpers/Modals/DateFilterModal';
import dayjs from 'dayjs';
import { useLocale } from '../../localization/LocaleProvider';
import InvoiceModal from '../Helpers/Modals/InvoiceModal';
import { parseInvoiceData } from '../RolfAdmin/invoiceCalculations';

export default function Billing(props) {
    const [invoices, setInvoices] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [v2Commissions, setV2Commissions] = useState(null);
    const [isUpdatingV2, setIsUpdatingV2] = useState(false);
    const [v2Error, setV2Error] = useState('');
    const [session, setSession] = useState(null);
    const [partner, setPartner] = useState(null);
    const Api = useApi();
    const [searchFilter, setSearchFilter] = useState({searchString: "", fromDate: dayjs().startOf("year"), toDate: dayjs().endOf("year")});
    const [showFilterModal, setShowFilterModal] = useState(false);
    const { translate } = useLocale();
    const [modalData, setModalData] = useState({visible: false, invoice: null});
    const [searchParams] = useSearchParams();
    const previewPartnerId = Number(searchParams.get('previewPartnerId')) || null;
    const isPreview = !!(previewPartnerId && session?.rolf);
    const effectivePartnerId = isPreview ? previewPartnerId : session?.partnerId;

    useEffect(() => {
        Api.getSession().then(session => {
            if (!session?.ID) {
                setSession(false);
                return;
            }

            Api.get(`/administrators/${session.ID}`).then(user => {
                setSession(user);
              })
        })
    }, []);

    useEffect(() => {
        if(!session) {
            return;
        }

        fetchPartner();
    }, [session]);

    useEffect(() => {
        if(!effectivePartnerId) {
            return;
        }

        fetchInvoices();
        fetchBillingV2Commissions();
    }, [effectivePartnerId]);

    function fetchPartner() {
        if (!effectivePartnerId) {
            setPartner(null);
            return;
        }
        Api.get(`/rolf/partners/${effectivePartnerId}`).then(response => {
            setPartner(response);
        }).catch(() => {});
    }

    function fetchInvoices() {
        Api.get("/rolf/invoices").then((result) => {
            setInvoices(result);
            setLoading(false);
        }
        ).catch(() => {
            setInvoices([]);
            setLoading(false);
        })
    }

    function fetchBillingV2Commissions() {
        setV2Error('');
        Api.get('/rolf/billing-v2/commissions').then((result) => {
            setV2Commissions(result);
        }).catch((error) => {
            setV2Commissions([]);
            setV2Error(error?.errorMessage || 'Kunde inte hämta provisionsposter från Fakturering 2.');
        });
    }

    function updateInvoice(id, invoicedValue) {
        if (isPreview) return;

        setIsUpdating(true);
        Api.post(`/rolf/partners/${partner.ID}/invoices/${id}/commissionStatus`, { commissionInvoiced: invoicedValue })
        .then((result) => {
                fetchInvoices(); //Refresh the data
            }).catch(() => {}).finally(() => {
                setIsUpdating(false);
            });
    }

    function markBillingV2CommissionInvoiced(commission) {
        if (isPreview) return;
        setIsUpdatingV2(true);
        setV2Error('');
        Api.post(`/rolf/billing-v2/commissions/${commission.ID}/mark-invoiced`, {})
            .then(() => {
                fetchBillingV2Commissions();
            }).catch((error) => {
                setV2Error(error?.errorMessage || 'Kunde inte markera provisionen som fakturerad.');
            }).finally(() => {
                setIsUpdatingV2(false);
            });
    }


    function sumOutstandingAmount(invoices) {
        return invoices.reduce((total, invoice) => {
            const parsedData = parseInvoiceData(invoice);
            return total + (parsedData?.outstandingCommission || 0);
        }, 0)
    }

    function showInvoice(invoice) {
        setModalData({visible: true, invoice: invoice});
    }

    function closeModal() {
        setModalData({visible: false, invoice: null});
    }

    const partnerInvoices = (invoices || []).filter((invoice) => {
        try {
            const invoiceData = parseInvoiceData(invoice);
            return Number(invoiceData?.partnerId) === Number(effectivePartnerId);
        } catch (e) {
            return false;
        }
    });

    const filteredInvoices = partnerInvoices.filter((invoice) => {
        let shouldBeIncluded = true;

        shouldBeIncluded = shouldBeIncluded && (searchFilter.searchString ? invoice.invoiceData.toLowerCase().includes(searchFilter.searchString.toLowerCase()) : true);
        shouldBeIncluded = shouldBeIncluded && (searchFilter.fromDate ? !dayjs.unix(invoice.invoiceDate).isBefore(dayjs(searchFilter.fromDate), 'day') : true);
        shouldBeIncluded = shouldBeIncluded && (searchFilter.toDate ? !dayjs.unix(invoice.invoiceDate).isAfter(dayjs(searchFilter.toDate), 'day') : true);

        return shouldBeIncluded;
    });

    const partnerV2Commissions = (v2Commissions || []).filter((commission) => Number(commission.partnerId) === Number(effectivePartnerId));
    const formatCommissionRate = (commission) => {
        const recurringRate = Number(commission.recurringRate || 0);
        const setupFeeRate = Number(commission.setupFeeRate || 0);
        return recurringRate === setupFeeRate ? `${recurringRate} %` : `${recurringRate} % löpande / ${setupFeeRate} % startavgift`;
    };
    const outstandingCommission = (commission) => commission.status === 'paid' ? 0 : Number(commission.amount || 0);
    const dateOnly = (value) => value ? String(value).slice(0, 10) : '–';

    if (loading) {
        return <div>{translate("Loading...")}</div>
    }

    return (<>

        <section className='grow'>
            <h4>{translate("Billing overview")}</h4>
            {isPreview && <p className='formControlSuccess'>Förhandsvisning av partnerläget för {partner?.name || 'vald partner'}. Åtgärder är avstängda.</p>}
            <p>{translate("Here you can see all invoices on all your clients, and their status.")}</p>
            <p>{translate("Send commission invoices to Rolf Nordic AB, org. number: 556962-6046. Invoice is sent by email to:")} <a href="mailto:inbox.lev.366358@arkivplats.se">inbox.lev.366358@arkivplats.se</a></p>

            <a href="#" onClick={() => setShowFilterModal(true)}><i className="fa fa-filter" aria-hidden="true"></i> {translate("Date Filter")}</a> <span className='meta'>{dayjs(searchFilter.fromDate).format("YYYY-MM-DD")} - {dayjs(searchFilter.toDate).format("YYYY-MM-DD")}</span>
            <FilterModal isOpen={showFilterModal} onClose={() => setShowFilterModal(false)} searchFilter={searchFilter} setSearchFilter={setSearchFilter} />

            <SearchClients searchFilter={searchFilter.searchString} setSearchFilter={(string) => setSearchFilter((prev) => {return {...prev, searchString: string}})} />

            {!invoices ? null :
                <table>
                    <thead>
                        <tr>
                            <th>{translate("Customer")}</th>
                            <th>{translate("Invoice date")}</th>
                            <th>Fakturavärde exkl. moms</th>
                            <th>{translate("Partner")}</th>
                            <th>{translate("Commission (%)")}</th>
                            <th>{translate("Commission (sek)")}</th>
                            <th>{translate("Customer invoce paid")}</th>
                            <th>{translate("Commission invoiced")}</th>
                            <th>{translate("Commission paid")}</th>
                            <th>{translate("Outstanding commission")}</th>
                            <th>{translate("Details")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredInvoices.length === 0 ? <tr><td colSpan={12}>{translate("No invoices, yet")}</td></tr> : filteredInvoices.map((invoice, index) => {
                            const invoiceData = parseInvoiceData(invoice);
                            return (
                                <tr key={index}>
                                    <td><Link to={`/rolf/clients/${invoice.clientId}`}>{invoiceData.customerName}</Link></td>
                                    <td>{moment.unix(invoice.invoiceDate).format("YYYY-MM-DD")}</td>
                                    <td>{invoiceData.value}</td>
                                    <td>{invoiceData.partnerName}</td>
                                    <td>{invoiceData.commissionPercentage}</td>
                                    <td>{invoiceData.commission}</td>
                                    <td><input type='checkbox' checked={invoiceData.invoicePaid} disabled={true}></input></td>
                                    <td><input type='checkbox' checked={invoiceData.commissionInvoiced} disabled={isPreview || isUpdating || !invoiceData.invoicePaid} onChange={(e) => updateInvoice(invoice.ID, e.target.checked)}></input></td>
                                    <td><input type='checkbox' checked={invoiceData.commissionPaid} disabled={true}></input></td>
                                    <td>{invoiceData.outstandingCommission}</td>
                                    <td><a href="#" onClick={() => showInvoice(invoice)}><i className="fa fa-eye" aria-hidden="true"></i></a></td>
                                </tr>
                            )
                        })}
                        <tr>
                            <td colSpan={3}><b>{translate("Total outstanding commission:")}</b></td>
                            <td colSpan={8} style={{ textAlign: "right" }}><b>{sumOutstandingAmount(filteredInvoices)}</b></td>
                        </tr>
                    </tbody>
                </table>
            }
            <p className='meta'>{translate("Check \"Commission invoiced\" to mark the commission as invoiced when you have sent your commission invoice to Rolf Nordic AB.")}</p>

            <h4>Fakturering 2 – provision</h4>
            <p>{translate("These commission entries are created when a Fakturering 2 invoice is finalised. You can mark a commission as invoiced after the customer invoice has been paid.")}</p>
            {v2Error && <p className='formControlError'>{v2Error}</p>}
            {!v2Commissions ? <p>{translate("Loading...")}</p> : !partnerV2Commissions.length ? <p>{translate("No invoices, yet")}</p> :
                <table>
                    <thead>
                        <tr>
                            <th>{translate("Customer")}</th>
                            <th>{translate("Period")}</th>
                            <th>{translate("Invoice date")}</th>
                            <th>Fakturavärde exkl. moms</th>
                            <th>{translate("Customer invoce paid")}</th>
                            <th>{translate("Commission (%)")}</th>
                            <th>{translate("Commission (sek)")}</th>
                            <th>{translate("Status")}</th>
                            <th>{translate("Outstanding commission")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {partnerV2Commissions.map((commission) => (
                            <tr key={commission.ID}>
                                <td>{commission.clientName}</td>
                                <td>{commission.period}</td>
                                <td>{dateOnly(commission.issuedDate)}</td>
                                <td>{Number(commission.invoiceSubtotal || 0).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {commission.currency}</td>
                                <td>{commission.invoiceStatus === 'paid' ? translate('Yes') : translate('No')}</td>
                                <td>{formatCommissionRate(commission)}</td>
                                <td>{Number(commission.amount || 0).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {commission.currency}</td>
                                <td>{commission.status === 'accrued' ? translate('Accrued') : commission.status === 'invoiced' ? translate('Commission invoiced') : translate('Commission paid')}</td>
                                <td>{outstandingCommission(commission).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {commission.currency}</td>
                                <td>{commission.status === 'accrued' && commission.invoiceStatus === 'paid' ?
                                    <button className='small' disabled={isPreview || isUpdatingV2} onClick={() => markBillingV2CommissionInvoiced(commission)}>{translate('Mark as invoiced')}</button> :
                                    commission.status === 'accrued' ? <span className='meta'>{translate('Waiting for customer payment')}</span> : null}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            }

            <InvoiceModal modalData={modalData} closeModal={closeModal} onRefresh={fetchInvoices} />

        </section>
    </>
    )
}
